import React from 'react';
import { Box, makeStyles, Theme } from '@material-ui/core';
import AppBar from 'components/@layout/AppBar';
import ReportFilter from './components/ReportFilter';
import PrintViewProvider from './providers/PrintViewProvider';
import ErrorBoundary from 'components/Error/ErrorBoundary';
import ReportFilterFallback from './components/common/ReportFilterFallback';
import KeyboardingReport from './components/KeyboardingReport';
import PrmReport from './components/PrmReport';
import useReportQueryParams from './hooks/use-report-query-params';
import WdtReport from './components/WdtReport';
import { ModuleProps } from '../../types';
import { ReportProduct } from './types';
import { useQueryParam } from 'use-query-params';
import EmptyResultSetMessage from 'components/@common/EmptyResultSetMessage';
import PrintProvider, { usePrint } from './providers/PrintProvider';

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      padding: theme.spacing(4),
      '& > header': {
        marginBottom: theme.spacing(2)
      },
      '& > section': {
        margin: `${theme.spacing(2)}px 0`,
        breakInside: 'avoid',
        '& > article': {
          margin: `${theme.spacing(2)}px 0`
        }
      }
    }
  };
});

const useIsInitialEmptyState = () => {
  const [query] = useQueryParam('productFamilyCode');
  return !query;
};

const PRODUCT_REPORT: { [key in ReportProduct]?: any } = {
  [ReportProduct.KEY]: KeyboardingReport,
  [ReportProduct.PRM]: PrmReport,
  [ReportProduct.WDT]: WdtReport
};

const Reports: React.FC<ModuleProps> = props => {
  const classes = useStyles();
  const [{ productFamilyCode }] = useReportQueryParams();
  const isInitialEmptyState = useIsInitialEmptyState();
  const [printRef] = usePrint();
  const ProductReport = PRODUCT_REPORT[productFamilyCode];
  if (!ProductReport) return null;

  return (
    <PrintViewProvider cardChartWidth={300} fullChartWidth={850}>
      <Box displayPrint="none">
        <AppBar {...props} />
      </Box>
      <ErrorBoundary fallbackComponent={ReportFilterFallback}>
        <ReportFilter />
        <ErrorBoundary fallbackComponent={ReportFilterFallback}>
          {isInitialEmptyState ? (
            <EmptyResultSetMessage
              style={{ marginTop: '16px' }}
              message="Generate a Report"
              subtitle='Select the product and filters, then click the "Generate Report" button to view the report.'
            />
          ) : (
            <div className={classes.root} ref={printRef}>
              <ProductReport />
            </div>
          )}
        </ErrorBoundary>
      </ErrorBoundary>
    </PrintViewProvider>
  );
};

export default (props: any) => (
  <PrintProvider>
    <Reports {...props} />
  </PrintProvider>
);
